import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Server, Search, RefreshCw, Monitor, HardDrive, Cpu, Trash2,
  MoreHorizontal, CheckCircle, AlertTriangle, X as XIcon, Download,
  Shield as ShieldIcon, Heart,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getSystemStatusSummary } from "@/lib/serverStatus";
import { ServerTagManager, TagFilterBar } from "@/components/ServerTagManager";
import { exportServerReport } from "@/lib/exportUtils";
import { SecurityScoreBadge } from "@/components/SecurityScoreBadge";
import { PageHead, StatTile, Section } from "@/components/dash/primitives";

export default function ServersPage() {
  const { profile } = useAuth();
  const [servers, setServers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  // Derive all unique tags from servers
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    servers.forEach((s) => (s.tags || []).forEach((t: string) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [servers]);

  // Filter servers by search + tags
  const filteredServers = useMemo(() => {
    return servers.filter((server) => {
      const matchesSearch =
        server.hostname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        server.ip_address.includes(searchTerm);

      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.every((tag) => (server.tags || []).includes(tag));

      return matchesSearch && matchesTags;
    });
  }, [servers, searchTerm, selectedTags]);

  useEffect(() => {
    if (profile?.tenant_id) {
      fetchServers();
      const cleanup = setupRealtimeSubscription();
      return cleanup;
    }
  }, [profile]);

  const fetchServers = async () => {
    try {
      const { data: serversData, error } = await supabase
        .from('v_server_status')
        .select('*')
        .eq('tenant_id', profile.tenant_id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const hostnames = serversData?.map((s: any) => s.hostname) || [];
      let latestMetrics: Record<string, any> = {};
      
      if (hostnames.length > 0) {
        const metricsPromises = hostnames.map(async (hostname: string) => {
          const { data } = await supabase
            .from('metrics_history')
            .select('hostname, secure_boot_enabled, tpm_enabled, firewall_enabled, windows_defender_enabled, windows_defender_updated, failed_login_last_24h, admin_accounts_count')
            .eq('tenant_id', profile.tenant_id)
            .eq('hostname', hostname)
            .order('timestamp', { ascending: false })
            .limit(1)
            .maybeSingle();
          return data;
        });

        const metricsResults = await Promise.all(metricsPromises);
        for (const m of metricsResults) {
          if (m?.hostname) latestMetrics[m.hostname] = m;
        }
      }

      // Also fetch tags from the servers table directly
      const { data: serverTags } = await (supabase.from('servers') as any)
        .select('hostname, tags')
        .eq('tenant_id', profile.tenant_id);

      const tagsMap: Record<string, string[]> = {};
      (serverTags || []).forEach((s: any) => {
        tagsMap[s.hostname] = s.tags || [];
      });

      const mergedServers = (serversData || []).map((server: any) => ({
        ...server,
        ...latestMetrics[server.hostname],
        id: server.id,
        status: server.status,
        online: server.online,
        tags: tagsMap[server.hostname] || [],
      }));

      setServers(mergedServers);
    } catch (error) {
      console.error('Error fetching servers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteServer = async (serverId: string, hostname: string) => {
    try {
      const { error } = await supabase.from('servers').delete().eq('id', serverId);
      if (error) throw error;
      toast({ title: "Server deleted", description: `${hostname} has been removed.` });
    } catch (error) {
      console.error('Error deleting server:', error);
      toast({ title: "Error deleting", description: "Something went wrong.", variant: "destructive" });
    }
  };

  const setupRealtimeSubscription = () => {
    if (!profile?.tenant_id) return () => {};

    const channel = supabase
      .channel('servers-realtime')
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'servers', filter: `tenant_id=eq.${profile.tenant_id}` }, () => fetchServers())
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'servers', filter: `tenant_id=eq.${profile.tenant_id}` }, () => fetchServers())
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'servers', filter: `tenant_id=eq.${profile.tenant_id}` }, (payload) => {
        setServers(prev => prev.filter(server => server.id !== payload.old.id));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchServers();
  };

  const handleTagsChange = (serverId: string, newTags: string[]) => {
    setServers(prev => prev.map(s => s.id === serverId ? { ...s, tags: newTags } : s));
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      online: { variant: "default" as const, className: "bg-success text-success-foreground" },
      offline: { variant: "secondary" as const, className: "" },
      warning: { variant: "default" as const, className: "bg-warning text-warning-foreground" },
      critical: { variant: "destructive" as const, className: "" }
    };
    return variants[status as keyof typeof variants] || variants.offline;
  };

  const formatUptime = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading) {
    return (
      <>
        <PageHead title="Servers" subtitle="Loading servers..." />
        <div className="stat-grid">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card" style={{ opacity: 0.5 }}>
              <div className="stat-card__label">Loading</div>
              <div className="stat-card__value">—</div>
            </div>
          ))}
        </div>
      </>
    );
  }

  // Stats
  const total = servers.length;
  const online = servers.filter((s) => s.online).length;
  const offline = total - online;
  const avgHealth = total
    ? Math.round(
        servers.reduce((acc, s) => {
          if (s.online) return acc + 100;
          if (s.status === "warning") return acc + 50;
          return acc;
        }, 0) / total
      )
    : 0;

  return (
    <>
      <PageHead
        title="Servers"
        subtitle="Manage and monitor your server infrastructure"
        right={
          <>
            <button
              className="btn"
              onClick={() => exportServerReport(filteredServers)}
              disabled={filteredServers.length === 0}
            >
              <Download size={14} /> Export CSV
            </button>
            <button className="btn" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
          </>
        }
      />

      <div className="stat-grid">
        <StatTile
          label="Total servers"
          value={total}
          sub="Monitored hosts"
          icon={<Server size={16} />}
        />
        <StatTile
          label="Online"
          value={online}
          sub="Reporting"
          tone="good"
          icon={<CheckCircle size={16} />}
        />
        <StatTile
          label="Offline"
          value={offline}
          sub="Not reporting"
          tone="bad"
          icon={<XIcon size={16} />}
        />
        <StatTile
          label="Avg health"
          value={`${avgHealth}%`}
          sub="Across fleet"
          tone={avgHealth >= 80 ? "good" : avgHealth >= 50 ? "warn" : "bad"}
          icon={<ShieldIcon size={16} />}
        />
      </div>

      <Section
        title="Filters"
        subtitle="Search and filter your servers"
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12 }}>
          <div className="topbar__search" style={{ maxWidth: "none" }}>
            <Search size={14} />
            <input
              placeholder="Search servers by name or IP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div />
        </div>
        <div style={{ marginTop: 12 }}>
          <TagFilterBar
            allTags={allTags}
            selectedTags={selectedTags}
            onSelectedTagsChange={setSelectedTags}
          />
        </div>
      </Section>

      <Section
        title="Server list"
        subtitle={`${filteredServers.length} of ${servers.length} servers`}
      >
        {filteredServers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 0", color: "var(--fg-3)" }}>
            <Server size={36} style={{ margin: "0 auto 12px", opacity: 0.5 }} />
            <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
              {servers.length === 0 ? "No servers yet" : "No servers match"}
            </p>
            <p style={{ fontSize: 13 }}>
              {servers.length === 0
                ? "Install monitoring agents to see them here."
                : "Try a different search."}
            </p>
          </div>
        ) : (
          filteredServers.map((server) => {
            const tone = server.online ? "ok" : "bad";
            const cpu = server.cpu_usage ?? 0;
            const mem = server.memory_usage ?? 0;
            const disk = server.disk_usage ?? 0;
            const issues = getSystemStatusSummary(server);
            const cpuColor =
              cpu > 80 ? "var(--bad)" : cpu > 50 ? "var(--warn)" : "var(--good)";
            return (
              <div key={server.id} className="server-row">
                <div className="server-row__main" style={{ cursor: "default" }}>
                  <div className={"server-status server-status--" + tone}>
                    {server.online ? (
                      <CheckCircle size={16} />
                    ) : (
                      <XIcon size={16} />
                    )}
                  </div>
                  <div className="server-id">
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <b>{server.hostname}</b>
                      <SecurityScoreBadge server={server} compact />
                    </div>
                    <div className="server-id__meta">
                      <span>
                        {server.ip_address}
                        {server.port ? `:${server.port}` : ""} · {server.os || "—"}
                      </span>
                    </div>
                    {!!(server.tags?.length) && (
                      <div style={{ marginTop: 6 }}>
                        <ServerTagManager
                          serverId={server.id}
                          tags={server.tags || []}
                          onTagsChange={(tags) => handleTagsChange(server.id, tags)}
                          allTags={allTags}
                        />
                      </div>
                    )}
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">CPU</span>
                    <span className="server-metric__value" style={{ color: cpuColor }}>
                      {cpu.toFixed(1)}%
                    </span>
                    <div className="server-metric__bar">
                      <div
                        style={{
                          width: cpu + "%",
                          background: cpuColor,
                          boxShadow: `0 0 6px ${cpuColor}`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">RAM</span>
                    <span className="server-metric__value">{mem.toFixed(1)}%</span>
                    <div className="server-metric__bar">
                      <div style={{ width: mem + "%", background: "var(--accent)" }} />
                    </div>
                  </div>
                  <div className="server-metric">
                    <span className="server-metric__label">DISK</span>
                    <span className="server-metric__value">{disk.toFixed(1)}%</span>
                    <div className="server-metric__bar">
                      <div style={{ width: disk + "%", background: "var(--accent-2)" }} />
                    </div>
                  </div>
                  <div>
                    <div className={"status-chip status-chip--" + tone}>
                      <span className="status-chip__dot" />
                      {server.online ? "online" : "offline"}
                    </div>
                    <div
                      style={{
                        fontFamily: "Geist Mono, monospace",
                        fontSize: 11,
                        color: "var(--fg-3)",
                        marginTop: 6,
                      }}
                    >
                      {formatUptime(server.uptime_seconds)} ·{" "}
                      {server.last_seen
                        ? new Date(server.last_seen).toLocaleString()
                        : "Never"}
                    </div>
                    {issues.length > 0 && (
                      <div
                        style={{
                          marginTop: 6,
                          fontSize: 11,
                          color: "var(--warn)",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <AlertTriangle size={12} /> {issues.length} issue
                        {issues.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="icon-btn" aria-label="Server actions">
                        <MoreHorizontal size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={(e) => e.preventDefault()}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete server
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Server</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete{" "}
                              <strong>{server.hostname}</strong>? This action cannot
                              be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleDeleteServer(server.id, server.hostname)
                              }
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })
        )}
      </Section>
    </>
  );
}
